import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { CommentDto, CommentReferenceType, UserOrganizationRole } from "@blinkbooks/types";
import { getComments, createComment, deleteComment } from "@/services/comment.service";
import { CommentThread } from "@/components/CommentThread";
import { CommentComposeBox } from "@/components/CommentComposeBox";
import posthog from "@/lib/posthog";

export interface CommentsSheetTarget {
  referenceType: CommentReferenceType;
  referenceId: string;
  label: string;
}

interface CommentsSheetProps {
  target: CommentsSheetTarget | null;
  currentUserId?: string;
  role?: UserOrganizationRole | null;
  onClose: () => void;
  onCommentCountChange?: (referenceId: string, count: number) => void;
}

const CommentsSheet = ({ target, currentUserId, role, onClose, onCommentCountChange }: CommentsSheetProps) => {
  const [comments, setComments] = useState<CommentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [replyTarget, setReplyTarget] = useState<CommentDto | null>(null);

  useEffect(() => {
    if (!target) {
      setComments([]);
      setReplyTarget(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setReplyTarget(null);
    getComments(target.referenceType, target.referenceId)
      .then((data) => {
        if (!cancelled) {
          setComments(data);
          onCommentCountChange?.(target.referenceId, data.length);
          posthog.capture("comments_sheet_opened", {
            reference_type: target.referenceType,
            existing_comment_count: data.length,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setComments([]);
          onCommentCountChange?.(target.referenceId, 0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.referenceType, target?.referenceId]);

  if (!target) return null;

  const handleSubmit = async (text: string, parentCommentId?: string) => {
    const created = await createComment({
      referenceType: target.referenceType,
      referenceId: target.referenceId,
      text,
      parentCommentId,
    });
    setComments((prev) => {
      const next = [...prev, created];
      onCommentCountChange?.(target.referenceId, next.length);
      return next;
    });
    posthog.capture("comment_created", {
      reference_type: target.referenceType,
      is_reply: !!parentCommentId,
    });
  };

  const handleDelete = async (id: string) => {
    const deletedComment = comments.find((c) => c.id === id);
    setComments((prev) => {
      const next = prev.filter((c) => c.id !== id);
      onCommentCountChange?.(target.referenceId, next.length);
      return next;
    });
    try {
      await deleteComment(id);
      posthog.capture("comment_deleted", {
        reference_type: target.referenceType,
        deleted_by: deletedComment?.authorUserId === currentUserId ? "author" : "moderator",
      });
    } catch {
      // Re-sync from server if the optimistic removal turns out to be wrong.
      getComments(target.referenceType, target.referenceId).then((data) => {
        setComments(data);
        onCommentCountChange?.(target.referenceId, data.length);
      });
    }
  };

  return (
    <Sheet open={!!target} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto flex flex-col">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="text-lg leading-tight">Comments</SheetTitle>
          <p className="text-sm text-muted-foreground">{target.label}</p>
        </SheetHeader>

        <div className="flex-1 py-4">
          {loading ? (
            <div className="flex items-center gap-2 py-10 justify-center text-muted-foreground">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-sm">Loading comments...</span>
            </div>
          ) : (
            <CommentThread
              comments={comments}
              currentUserId={currentUserId}
              role={role}
              onReply={(comment) => {
                setReplyTarget(comment);
                posthog.capture("comment_reply_clicked", { reference_type: target.referenceType });
              }}
              onDelete={handleDelete}
            />
          )}
        </div>

        <div className="pt-3 border-t sticky bottom-0 bg-background">
          <CommentComposeBox
            replyTarget={replyTarget ? { id: replyTarget.id, authorName: replyTarget.authorName, text: replyTarget.text } : null}
            onCancelReply={() => setReplyTarget(null)}
            onSubmit={handleSubmit}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CommentsSheet;
